import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import {
  Check,
  X,
  Pencil,
  Loader2,
  CheckCircle2,
  Clock,
  Send,
} from 'lucide-react';
import { api, ApiError } from '../lib/api';
import { PLATFORM_META, type Platform } from '../lib/platforms';
import { AccountAvatar } from '../components/AccountAvatar';
import { PlatformPreview, type PreviewKind } from '../components/platformPreviews';
import { cn } from '../lib/cn';

type Decision = 'approved' | 'changes_requested' | 'rejected';
type RequestStatus = 'pending' | 'partially_approved' | 'approved' | 'changes_requested' | 'rejected';

type ReviewTarget = {
  id: string;
  kind: string;
  caption: string | null;
  approvalStatus: 'pending' | Decision;
  approvalNote: string | null;
  published: boolean;
  platform: Platform;
  username: string | null;
  displayName: string | null;
  profilePhotoUrl: string | null;
};

type ReviewPost = {
  id: string;
  caption: string;
  media: Array<{ url: string; type?: string }>;
  scheduledAt: string | null;
  targets: ReviewTarget[];
};

type ReviewData = {
  request: { title: string | null; message: string | null; status: RequestStatus; clientName: string | null; reviewedAt: string | null };
  posts: ReviewPost[];
};

type LocalDecision = { decision: Decision | null; note: string };

export function ApprovalReview() {
  const { token } = useParams<{ token: string }>();
  const [name, setName] = useState('');
  const [choices, setChoices] = useState<Record<string, LocalDecision>>({});
  const [done, setDone] = useState(false);

  const reviewQuery = useQuery({
    queryKey: ['public-approval', token],
    queryFn: async () => {
      const data = await api<ReviewData>(`/api/public/approvals/${token}`);
      // Seed local choices from any decisions already on record.
      const seed: Record<string, LocalDecision> = {};
      for (const p of data.posts) {
        for (const t of p.targets) {
          seed[t.id] = {
            decision: t.approvalStatus === 'pending' ? null : t.approvalStatus,
            note: t.approvalNote ?? '',
          };
        }
      }
      setChoices(seed);
      if (data.request.clientName) setName(data.request.clientName);
      return data;
    },
    enabled: Boolean(token),
    retry: false,
  });

  const decideMutation = useMutation({
    mutationFn: (decisions: Array<{ postId: string; targetId: string; decision: Decision; note: string | null }>) =>
      api(`/api/public/approvals/${token}/decide`, {
        method: 'POST',
        body: JSON.stringify({ clientName: name.trim() || null, decisions }),
      }),
    onSuccess: () => setDone(true),
  });

  const allTargets = useMemo(
    () => (reviewQuery.data?.posts ?? []).flatMap((p) => p.targets.map((t) => ({ postId: p.id, t }))),
    [reviewQuery.data],
  );
  const decidableTargets = allTargets.filter(({ t }) => !t.published);
  const decidedCount = decidableTargets.filter(({ t }) => choices[t.id]?.decision).length;
  const allDecided = decidableTargets.length > 0 && decidedCount === decidableTargets.length;

  function setChoice(targetId: string, patch: Partial<LocalDecision>) {
    setChoices((prev) => ({ ...prev, [targetId]: { decision: prev[targetId]?.decision ?? null, note: prev[targetId]?.note ?? '', ...patch } }));
  }

  function submit() {
    const decisions = decidableTargets
      .filter(({ t }) => choices[t.id]?.decision)
      .map(({ postId, t }) => ({
        postId,
        targetId: t.id,
        decision: choices[t.id]!.decision!,
        note: choices[t.id]!.note.trim() || null,
      }));
    if (decisions.length > 0) decideMutation.mutate(decisions);
  }

  if (reviewQuery.isLoading) {
    return <Shell><div className="flex items-center justify-center py-20 text-slate-400"><Loader2 className="animate-spin" size={28} /></div></Shell>;
  }

  if (reviewQuery.isError) {
    const notFound = reviewQuery.error instanceof ApiError && reviewQuery.error.status === 404;
    return (
      <Shell>
        <div className="text-center py-16">
          <h1 className="text-xl font-bold text-slate-900">{notFound ? 'Enlace no válido' : 'Algo ha ido mal'}</h1>
          <p className="mt-2 text-slate-600">
            {notFound
              ? 'Este enlace de aprobación no existe o ha caducado. Pide uno nuevo a quien te lo compartió.'
              : 'No hemos podido cargar la revisión. Inténtalo de nuevo en unos minutos.'}
          </p>
        </div>
      </Shell>
    );
  }

  const data = reviewQuery.data!;

  if (done) {
    return (
      <Shell>
        <div className="text-center py-12">
          <div className="mx-auto w-16 h-16 rounded-full flex items-center justify-center text-emerald-600 bg-emerald-50">
            <CheckCircle2 size={32} />
          </div>
          <h1 className="mt-5 text-2xl font-bold text-slate-900">¡Gracias por tu revisión!</h1>
          <p className="mt-2 text-slate-600 max-w-md mx-auto">
            Hemos enviado tus decisiones al equipo. Las redes aprobadas se publicarán según lo previsto y las que tienen
            cambios pedidos quedan a la espera.
          </p>
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      <div className="mb-2">
        <span className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full text-amber-700 bg-amber-50">
          <Clock size={13} /> Pendiente de tu revisión
        </span>
        <h1 className="mt-3 text-2xl font-bold text-slate-900">
          {data.request.title?.trim() || 'Revisa estas publicaciones'}
        </h1>
        <p className="mt-1.5 text-slate-600">
          {data.posts.length === 1
            ? 'Aprueba cada red, pide cambios o recházala. La publicación no saldrá hasta que la apruebes.'
            : `${data.posts.length} publicaciones para revisar. Decide red por red: aprobar, pedir cambios o rechazar.`}
        </p>
        {data.request.message?.trim() && (
          <div className="mt-3 rounded-xl bg-brand-50 border border-brand-100 p-3 text-sm text-slate-700 whitespace-pre-wrap">
            {data.request.message}
          </div>
        )}
      </div>

      <div className="space-y-5 mt-5">
        {data.posts.map((post, i) => (
          <PostReviewCard
            key={post.id}
            post={post}
            index={i}
            total={data.posts.length}
            choices={choices}
            setChoice={setChoice}
          />
        ))}
      </div>

      {/* Sticky submit bar */}
      <div className="sticky bottom-0 -mx-6 mt-6 px-6 py-3 bg-white/95 backdrop-blur border-t border-slate-200 rounded-b-2xl">
        <div className="flex items-center gap-3 flex-wrap">
          <input
            className="input flex-1 min-w-[160px]"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Tu nombre (opcional)"
            maxLength={120}
          />
          <div className="text-xs text-slate-500 whitespace-nowrap">
            {decidedCount}/{decidableTargets.length} decididas
          </div>
          <button
            type="button"
            onClick={submit}
            disabled={decideMutation.isPending || decidedCount === 0}
            className="btn-primary gap-2"
          >
            {decideMutation.isPending ? <Loader2 className="animate-spin" size={15} /> : <Send size={15} />}
            Enviar decisiones
          </button>
        </div>
        {!allDecided && decidableTargets.length > 0 && (
          <p className="mt-1.5 text-[11px] text-slate-400">
            Puedes enviar solo algunas decisiones ahora y volver al enlace más tarde para el resto.
          </p>
        )}
        {decideMutation.isError && (
          <p className="mt-1.5 text-xs text-red-600">
            {decideMutation.error instanceof ApiError && decideMutation.error.code === 'ALREADY_REVIEWED'
              ? 'Esta revisión ya no admite cambios.'
              : 'No se pudieron enviar tus decisiones. Inténtalo de nuevo.'}
          </p>
        )}
      </div>
    </Shell>
  );
}

function PostReviewCard({
  post,
  index,
  total,
  choices,
  setChoice,
}: {
  post: ReviewPost;
  index: number;
  total: number;
  choices: Record<string, LocalDecision>;
  setChoice: (targetId: string, patch: Partial<LocalDecision>) => void;
}) {
  const [activeIdx, setActiveIdx] = useState(0);
  const active = post.targets[activeIdx] ?? post.targets[0];
  const previewMedia = post.media.map((m) => ({ url: m.url, kind: m.type === 'video' ? 'video' as const : 'image' as const }));

  return (
    <div className="rounded-2xl border border-slate-200 overflow-hidden">
      <div className="px-4 py-2.5 bg-slate-50 border-b border-slate-100 flex items-center justify-between">
        <span className="text-xs font-semibold text-slate-500">
          {total > 1 ? `Publicación ${index + 1} de ${total}` : 'Publicación'}
        </span>
        {post.scheduledAt && (
          <span className="text-xs text-slate-400">
            {new Date(post.scheduledAt).toLocaleString('es-ES', { dateStyle: 'medium', timeStyle: 'short' })}
          </span>
        )}
      </div>

      <div className="grid md:grid-cols-2 gap-4 p-4">
        {/* Preview with network tabs */}
        <div>
          <div className="flex flex-wrap gap-1.5 mb-3">
            {post.targets.map((t, i) => {
              const meta = PLATFORM_META[t.platform];
              const Icon = meta.icon;
              return (
                <button
                  key={t.id}
                  onClick={() => setActiveIdx(i)}
                  className={cn(
                    'inline-flex items-center gap-1.5 text-[11px] px-2 py-1 rounded-md border transition',
                    i === activeIdx ? 'border-brand-300 bg-brand-50 text-brand-700' : 'border-slate-200 text-slate-600 hover:border-slate-300',
                  )}
                >
                  <Icon size={12} color={meta.color} />
                  <span className="truncate max-w-[90px]">{t.username ?? t.displayName ?? meta.label}</span>
                  <StatusDot decision={choices[t.id]?.decision ?? null} published={t.published} />
                </button>
              );
            })}
          </div>
          {active && (
            <div className="flex justify-center bg-slate-50 rounded-xl p-3">
              <PlatformPreview
                account={active}
                kind={active.kind as PreviewKind}
                media={previewMedia}
                caption={active.caption && active.caption.trim().length > 0 ? active.caption : post.caption}
              />
            </div>
          )}
        </div>

        {/* Per-network decisions */}
        <div className="space-y-2.5">
          {post.targets.map((t) => (
            <NetworkDecisionRow
              key={t.id}
              target={t}
              choice={choices[t.id] ?? { decision: null, note: '' }}
              setChoice={setChoice}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

function NetworkDecisionRow({
  target,
  choice,
  setChoice,
}: {
  target: ReviewTarget;
  choice: LocalDecision;
  setChoice: (targetId: string, patch: Partial<LocalDecision>) => void;
}) {
  const meta = PLATFORM_META[target.platform];
  const showNote = choice.decision === 'changes_requested' || choice.decision === 'rejected';

  return (
    <div className="rounded-xl border border-slate-200 p-3">
      <div className="flex items-center gap-2.5 mb-2">
        <AccountAvatar account={target} size={30} />
        <div className="min-w-0 flex-1">
          <div className="text-sm font-medium text-slate-900 truncate">{target.displayName ?? target.username ?? meta.label}</div>
          <div className="text-xs text-slate-500">{meta.label}{target.kind !== 'post' && ` · ${target.kind}`}</div>
        </div>
        {target.published && (
          <span className="text-[11px] font-medium text-emerald-600 inline-flex items-center gap-1">
            <CheckCircle2 size={13} /> Publicado
          </span>
        )}
      </div>

      {target.published ? (
        <p className="text-xs text-slate-400">Esta red ya se publicó y no puede modificarse.</p>
      ) : (
        <>
          <div className="grid grid-cols-3 gap-1.5">
            <DecisionButton
              active={choice.decision === 'approved'}
              onClick={() => setChoice(target.id, { decision: 'approved' })}
              tone="emerald"
              icon={<Check size={14} />}
              label="Aprobar"
            />
            <DecisionButton
              active={choice.decision === 'changes_requested'}
              onClick={() => setChoice(target.id, { decision: 'changes_requested' })}
              tone="blue"
              icon={<Pencil size={14} />}
              label="Cambios"
            />
            <DecisionButton
              active={choice.decision === 'rejected'}
              onClick={() => setChoice(target.id, { decision: 'rejected' })}
              tone="red"
              icon={<X size={14} />}
              label="Rechazar"
            />
          </div>
          {showNote && (
            <textarea
              className="input mt-2 min-h-[60px] resize-y text-sm"
              value={choice.note}
              onChange={(e) => setChoice(target.id, { note: e.target.value })}
              placeholder={choice.decision === 'rejected' ? 'Motivo del rechazo (opcional)…' : 'Qué hay que cambiar…'}
              maxLength={2000}
            />
          )}
        </>
      )}
    </div>
  );
}

function DecisionButton({
  active, onClick, tone, icon, label,
}: {
  active: boolean;
  onClick: () => void;
  tone: 'emerald' | 'blue' | 'red';
  icon: React.ReactNode;
  label: string;
}) {
  const tones = {
    emerald: active ? 'bg-emerald-600 text-white border-emerald-600' : 'text-emerald-700 border-emerald-200 hover:bg-emerald-50',
    blue: active ? 'bg-blue-600 text-white border-blue-600' : 'text-blue-700 border-blue-200 hover:bg-blue-50',
    red: active ? 'bg-red-600 text-white border-red-600' : 'text-red-600 border-red-200 hover:bg-red-50',
  }[tone];
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn('inline-flex items-center justify-center gap-1.5 rounded-lg border px-2 py-1.5 text-xs font-medium transition', tones)}
    >
      {icon} {label}
    </button>
  );
}

function StatusDot({ decision, published }: { decision: Decision | null; published: boolean }) {
  if (published) return <CheckCircle2 size={12} className="text-emerald-500" />;
  const color = decision === 'approved' ? 'bg-emerald-500' : decision === 'changes_requested' ? 'bg-blue-500' : decision === 'rejected' ? 'bg-red-500' : 'bg-slate-300';
  return <span className={cn('h-1.5 w-1.5 rounded-full', color)} />;
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-slate-50">
      <header className="border-b border-slate-200 bg-white">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center justify-center">
          <img src="/zettapost-logo-min.png" alt="Zettapost" className="h-8 w-auto" />
        </div>
      </header>
      <main className="max-w-3xl mx-auto px-4 py-8">
        <div className="card">{children}</div>
        <p className="mt-4 text-center text-xs text-slate-400">
          Revisión segura mediante enlace privado 🔒 · Creado desde{' '}
          <a
            href="https://zettapost.com"
            target="_blank"
            rel="noreferrer"
            className="font-medium text-slate-500 hover:text-brand-600 underline underline-offset-2"
          >
            Zettapost
          </a>
        </p>
      </main>
    </div>
  );
}
